import { Editor, Element, Node, Path, Range } from 'slate';
import { isTag } from './navigation';
import { getBlockTagBindings, isBlockTagCarrierType, TagBinding, TEXT_BLOCK_TAG_CARRIER_TYPES } from '../types/block';

export interface ChainCarrier {
  path: Path;
  elementId: string;
}

const holdsInlineTag = (editor: Editor, path: Path, tagIds: Set<string>): boolean =>
  !Editor.nodes(editor, { at: path, match: (n) => isTag(n) && tagIds.has(n.tagId) }).next().done;

const selectsText = (editor: Editor, range: Range, path: Path): boolean => {
  const selected = Range.intersection(Editor.range(editor, path), range);
  return !!selected && Editor.string(editor, selected).trim() !== '';
};

// Enumerate the block-tag carriers a selection touches, in document order.
// Carriers come from the live editor tree (driven by `editor.lastSelection`),
// not the DOM selection — the latter is blind inside the shadow root.
// `conceptTagIds` (the document's existing tag ids for the chosen concept) are
// skipped per-carrier so we never stack a duplicate fact of the same concept;
// carriers bound only to other concepts are kept (stacking is allowed).
export function collectChainCarriers(editor: Editor, range: Range, opts: { conceptTagIds?: Set<string>; textCarriersOnly?: boolean } = {}): ChainCarrier[] {
  const entries = Array.from(
    Editor.nodes(editor, {
      at: range,
      match: (n) => Element.isElement(n) && isBlockTagCarrierType((n as { type?: string }).type),
    })
  ).filter(([node, path]) => {
    if (!TEXT_BLOCK_TAG_CARRIER_TYPES.has((node as { type?: string }).type ?? '')) return !opts.textCarriersOnly;
    return selectsText(editor, range, path);
  });
  const conceptTagIds = opts.conceptTagIds;
  return entries
    .filter(([node]) => !conceptTagIds || !getBlockTagBindings(node as Node).some((b) => conceptTagIds.has(b.tagId)))
    .filter(([, path]) => !conceptTagIds || !opts.textCarriersOnly || !holdsInlineTag(editor, path, conceptTagIds))
    .map(([node, path]) => ({ path, elementId: (node as { uuid?: string }).uuid ?? '' }));
}

// Build the Slate `tags[]` binding writes from the endpoint's ordered created
// tags: each carrier links forward to the next created tag; the tail links to
// an existing successor (concept-exists merge) or terminates (fresh chain).
export function bindingsFromResponse(carriers: ChainCarrier[], createdTags: { id: string }[], successorId?: string): { path: Path; binding: TagBinding }[] {
  return carriers.map((carrier, i) => {
    const tagId = createdTags[i].id;
    const continuedAt = i < createdTags.length - 1 ? createdTags[i + 1].id : successorId;
    return { path: carrier.path, binding: continuedAt ? { tagId, continuedAt } : { tagId } };
  });
}
